/** Section 8, Custom fields: the operator's own fields (`Pre_Check_In_Field`, shared by every
 *  tour that asks them) — one row each, the same geometry in both modes.
 *
 *  - Label: renamed here, it is renamed wherever it is asked — every row of the form, and
 *    every tour that asks it (the row says how many, amber, while renamed).
 *  - Type: locked once answers are stored (a lock icon and the count, the reason in its
 *    tooltip) — the server refuses the change then.
 *  - Used by: this form (as the draft leaves it) and the tours that ask it; Answers stored.
 *  - Remove: only when nothing uses it — not this form, no tour, no answer (the server's
 *    rule); otherwise disabled with the reason. A removed row stays in place, struck through
 *    on the amber tint, with Restore.
 *  - Custom fields this draft created (in a field list) are listed too, "created when you
 *    save"; their label and type are edited here like the others, removed from the list
 *    that asks them.
 *
 *  Changed-Items-Only: only the control a change concerns takes the amber tint, each row
 *  says its own state under it, with its own revert. A refusal of the server about one
 *  field ("“Shoe size”: …") shows under its row. */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, type ReactNode } from 'react';

import { FIELD_TYPES, type CustomFieldDefinition } from '@/features/precheck/model';
import { INPUT_SX, ROW_PX } from '@/features/precheck/editor/rows';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

import { useOperatorPrecheck } from './context';
import { withoutDefinitionPrefix } from './errors';
import {
  askedIds,
  asDefinitions,
  DEFINITIONS_PATH,
  fieldEdit,
  LABEL_MAX_LENGTH,
  LEAD_FIELDS,
  newCustomFields,
  PARTY_FIELDS,
  removeBlockedReason,
  typeLabel,
  typeLockReason,
} from './model';
import { useCoalesceSession, useOperatorDraft } from './widgets';

/** Label · Type · Used by · Answers · actions (room for a worded "Restore"). */
const COLS = 'minmax(160px, 1fr) 132px 148px 80px 96px';

interface RowModel {
  id: string;
  stored: CustomFieldDefinition | null;
  /** The draft's (null: removed). */
  now: CustomFieldDefinition | null;
}

export function CustomFieldsTable() {
  const { baseline, canEdit, storedIds } = useOperatorPrecheck();
  const { mode, errorsFor, conflictsFor, registerFocus } = useRecordEditor();
  const editing = mode === 'edit' && canEdit;
  const rec = baseline.rec;
  const { valueOf, apply } = useOperatorDraft(rec);
  const storedRow = baseline.paths[rec] ?? {};
  const stored = asDefinitions(storedRow[DEFINITIONS_PATH]);
  // View mode shows what is stored; edit mode the draft.
  const read = editing ? valueOf : (p: string) => storedRow[p];
  const now = asDefinitions(read(DEFINITIONS_PATH));
  const lists = [read(LEAD_FIELDS), read(PARTY_FIELDS)];
  const asked = askedIds(lists);
  const created = editing ? newCustomFields(lists, storedIds) : [];
  const rows: RowModel[] = [
    ...stored.map((s) => ({ id: s.id, stored: s, now: now.find((d) => d.id === s.id) ?? null })),
    ...created.map((c) => ({ id: c.id, stored: null, now: c })),
  ];

  // A refusal about one field lands under ITS row — keyed by the field's id (the core's item
  // key), never by its label, which two fields may share; the rest under the table.
  const problems = editing ? conflictsFor(rec, DEFINITIONS_PATH) : [];
  const rowErrors = (r: RowModel) => [
    ...new Set(problems.filter((p) => p.contact_key === r.id).map((p) => withoutDefinitionPrefix(p.message))),
  ];
  const loose = [
    ...new Set([
      ...problems.filter((p) => !rows.some((r) => r.id === p.contact_key)).map((p) => p.message),
      // Errors with no item of their own (an invalid input on the list).
      ...(editing ? errorsFor(rec, DEFINITIONS_PATH) : []).filter((e) => !problems.some((p) => p.message === e)),
    ]),
  ];

  // A ledger row ("Custom fields") lands on the first input.
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(
    () =>
      registerFocus(rec, DEFINITIONS_PATH, () => {
        tableRef.current?.querySelector<HTMLElement>('input:not([disabled]), [role="combobox"]')?.focus();
      }),
    [rec, registerFocus],
  );

  const setDefs = (next: CustomFieldDefinition[], label: string) => apply({ [DEFINITIONS_PATH]: next }, label);

  return (
    <Box ref={tableRef} role="table" aria-label="Custom fields" data-custom-fields sx={{ display: 'grid', minWidth: 0 }}>
      <Box role="row" sx={{ display: 'grid', gridTemplateColumns: COLS, columnGap: 1, alignItems: 'center', minHeight: 28 }}>
        {['Label', 'Type', 'Used by', 'Answers'].map((h) => (
          <Typography key={h} role="columnheader" variant="caption" color="text.secondary" sx={{ fontWeight: 600, px: h === 'Label' ? 1 : 0 }}>
            {h}
          </Typography>
        ))}
        <Box role="columnheader" aria-label="Actions" />
      </Box>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', minHeight: ROW_PX, px: 1 }}>
          No custom fields yet — add one to a form with “Add a field” → “New custom field…”.
        </Typography>
      )}
      {rows.map((r) => (
        <CustomFieldRow
          key={r.id}
          row={r}
          editing={editing}
          askedByForm={asked.has(r.id)}
          errors={rowErrors(r)}
          onRename={(name, coalesce) => apply(fieldEdit(valueOf, r.id, { name }, storedIds), `Rename ${r.now?.name ?? r.id}`, coalesce)}
          onRetype={(type) => apply(fieldEdit(valueOf, r.id, { type }, storedIds), `Make ${r.now?.name ?? r.id} ${typeLabel(type)}`)}
          onRemove={() => setDefs(now.filter((d) => d.id !== r.id), `Remove the custom field ${r.stored?.name ?? r.id}`)}
          onRestore={() => {
            // Back in its stored place.
            const order = stored.map((s) => s.id);
            setDefs(
              [...now, r.stored!].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)),
              `Restore the custom field ${r.stored?.name ?? r.id}`,
            );
          }}
          onRevert={() => setDefs(now.map((d) => (d.id === r.id && r.stored ? r.stored : d)), `Revert ${r.stored?.name ?? r.id}`)}
        />
      ))}
      {loose.map((e) => (
        <Typography key={e} variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', px: 1 }} data-row-issue="invalid">
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
          {e}
        </Typography>
      ))}
    </Box>
  );
}

function CustomFieldRow({
  row,
  editing,
  askedByForm,
  errors,
  onRename,
  onRetype,
  onRemove,
  onRestore,
  onRevert,
}: {
  row: RowModel;
  editing: boolean;
  askedByForm: boolean;
  errors: string[];
  onRename: (name: string, coalesce: string | null) => void;
  onRetype: (type: string) => void;
  onRemove: () => void;
  onRestore: () => void;
  onRevert: () => void;
}) {
  const { baseline } = useOperatorPrecheck();
  const session = useCoalesceSession(`precheck-definition:${row.id}`);
  const meta = row.stored ? baseline.definitions.get(row.id) : undefined;
  const removed = editing && row.stored !== null && row.now === null;
  const isNew = row.stored === null;
  const cur = row.now ?? row.stored!;
  const renamed = editing && Boolean(row.stored && row.now && row.now.name !== row.stored.name);
  const retyped = editing && Boolean(row.stored && row.now && row.now.type !== row.stored.type);
  const lock = typeLockReason(meta);
  const removeBlocked = removeBlockedReason(meta, askedByForm);
  const tours = meta?.used_by.tours ?? 0;
  const usedBy = [askedByForm ? 'this form' : null, tours > 0 ? `${tours} ${tours === 1 ? 'tour' : 'tours'}` : null].filter(Boolean).join(' · ');
  const answers = meta?.answers ?? 0;
  const types = FIELD_TYPES.some((t) => t.value === cur.type) ? FIELD_TYPES : [...FIELD_TYPES, { value: cur.type, label: cur.type }];

  const notes: Array<{ tone: 'warning' | 'error' | 'muted'; icon?: ReactNode; text: ReactNode }> = [];
  if (isNew) notes.push({ tone: 'warning', text: 'New — created when you save' });
  if (removed) notes.push({ tone: 'warning', text: `“${row.stored!.name}” is deleted when you save — Restore keeps it.` });
  if (renamed && tours > 0) notes.push({ tone: 'warning', text: `Asked by ${tours} ${tours === 1 ? 'tour' : 'tours'} — renaming it changes it on all of them` });
  if (renamed) notes.push({ tone: 'muted', text: `was “${row.stored!.name}”` });
  if (retyped) notes.push({ tone: 'muted', text: `was ${typeLabel(row.stored!.type)}` });
  for (const e of errors) notes.push({ tone: 'error', text: e });

  return (
    <Box
      role="row"
      data-custom-field={row.id}
      data-state={removed ? 'removed' : isNew ? 'added' : renamed || retyped ? 'changed' : 'stored'}
      sx={{
        display: 'grid',
        gridTemplateColumns: COLS,
        columnGap: 1,
        alignItems: 'center',
        borderTop: 1,
        borderColor: 'divider',
        borderRadius: removed ? 1 : 0,
        bgcolor: removed ? CHANGED_TINT : 'transparent',
        py: 0.5,
      }}
    >
      <Box role="cell" sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: ROW_PX }}>
        {editing && !removed ? (
          <TextField
            size="small"
            value={cur.name}
            onChange={(e) => onRename(e.target.value, session.token())}
            onFocus={session.begin}
            onBlur={session.end}
            inputProps={{ 'aria-label': `Label of the custom field ${row.id}`, maxLength: LABEL_MAX_LENGTH }}
            sx={[INPUT_SX, renamed && { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } }, isNew && { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } }]}
          />
        ) : (
          <Typography
            variant="body2"
            noWrap
            sx={{ px: 1, minWidth: 0, ...(removed ? { textDecoration: 'line-through' } : {}) }}
            title={row.id}
          >
            {cur.name}
          </Typography>
        )}
      </Box>
      <Box role="cell" sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {editing && !removed && !lock ? (
          <TextField
            select
            size="small"
            value={cur.type}
            onChange={(e) => onRetype(e.target.value)}
            SelectProps={{ inputProps: { 'aria-label': `Type of ${cur.name || row.id}` } }}
            sx={[{ width: '100%', '& .MuiInputBase-root': { height: 28, fontSize: '0.875rem' } }, retyped && { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } }]}
          >
            {types.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <Typography variant="body2" noWrap sx={removed ? { textDecoration: 'line-through' } : undefined}>
              {typeLabel(cur.type)}
            </Typography>
            {lock && (
              <Tooltip title={lock} describeChild>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }} data-type-locked>
                  <LockOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
                  <Typography variant="caption">locked</Typography>
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>
      <Typography role="cell" variant="body2" color={usedBy ? 'text.primary' : 'text.secondary'} noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {isNew ? 'this form' : usedBy || 'Not used'}
      </Typography>
      <Typography role="cell" variant="body2" color={answers > 0 ? 'text.primary' : 'text.secondary'} noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {isNew ? '—' : answers > 0 ? answers.toLocaleString('en') : 'none'}
      </Typography>
      <Box role="cell" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {editing && (renamed || retyped) && (
          <Tooltip title={`Revert to “${row.stored!.name}” · ${typeLabel(row.stored!.type)}`}>
            <IconButton size="small" aria-label={`Revert ${row.stored!.name}`} onClick={onRevert}>
              <ReplayIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {editing && removed && (
          // Worded: an icon alone (a bin with an arrow) read as delete.
          <Button
            size="small"
            startIcon={<RestoreIcon />}
            onClick={onRestore}
            aria-label={`Restore ${row.stored!.name}`}
            sx={{ py: 0, minHeight: 24 }}
            data-restore
          >
            Restore
          </Button>
        )}
        {editing && !removed && !isNew && (
          <Tooltip title={removeBlocked ?? 'Remove — deleted when you save'}>
            <span>
              <IconButton size="small" aria-label={`Remove ${cur.name || row.id}`} disabled={removeBlocked !== null} onClick={onRemove}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {notes.length > 0 && (
        <Box sx={{ gridColumn: '1 / -1', display: 'grid', gap: 0.25, px: 1, pb: 0.25 }} data-custom-field-notes>
          {notes.map((n, i) => (
            <Typography
              key={i}
              variant="caption"
              color={n.tone === 'error' ? 'error' : n.tone === 'warning' ? 'warning.dark' : 'text.secondary'}
              sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
              {...(n.tone === 'error' ? { 'data-row-issue': 'invalid' } : {})}
            >
              {n.tone === 'error' ? (
                <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
              ) : n.tone === 'warning' ? (
                <WarningAmberIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
              ) : null}
              {n.text}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

